import { useEffect, useRef } from 'react'
import { motion } from 'framer-motion'

interface SplashScreenProps {
  heroLogoRef: React.RefObject<HTMLImageElement>
}

const SplashScreen = ({ heroLogoRef }: SplashScreenProps) => {
  const splashLogoRef = useRef<HTMLImageElement>(null)
  const splashCircleRef = useRef<HTMLDivElement>(null)
  const splashContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Bloquear scroll durante el splash manteniendo la barra visible
    document.body.classList.add('no-scroll')

    // Esperar 1.2 segundos antes de iniciar la animación de movimiento
    const moveTimer = setTimeout(() => {
      if (!splashLogoRef.current || !heroLogoRef.current) return

      const startRect = splashLogoRef.current.getBoundingClientRect()
      const endRect = heroLogoRef.current.getBoundingClientRect()

      // Calcular el desplazamiento
      const deltaX = endRect.left - startRect.left
      const deltaY = endRect.top - startRect.top
      const scale = endRect.width / startRect.width

      // Aplicar transición al logo del splash - más rápida
      splashLogoRef.current.style.transition = 'transform 0.7s cubic-bezier(0.68, -0.55, 0.27, 1.55)'
      splashLogoRef.current.style.transformOrigin = 'top left'
      splashLogoRef.current.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${scale})`

      // Animar el círculo rojo
      if (splashCircleRef.current && splashContainerRef.current) {
        splashContainerRef.current.style.backgroundColor = 'transparent'

        const navCenter = {
          x: endRect.left + endRect.width / 2,
          y: endRect.top + endRect.height / 2,
        }
        const screenCenter = {
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
        }

        const circleDeltaX = navCenter.x - screenCenter.x
        const circleDeltaY = navCenter.y - screenCenter.y

        // Configurar el estado inicial del círculo
        splashCircleRef.current.style.transition = 'none'
        splashCircleRef.current.style.transform = 'scale(200)'
        splashCircleRef.current.getBoundingClientRect() // Force reflow

        // Aplicar la animación - más rápida
        splashCircleRef.current.style.transition = 'transform 0.8s ease-in-out'
        splashCircleRef.current.style.transformOrigin = 'center center'
        splashCircleRef.current.style.transform = `translate(${circleDeltaX}px, ${circleDeltaY}px) scale(0)`

        splashContainerRef.current.style.pointerEvents = 'none'
      }

      // Mostrar el logo real después de 0.7 segundos (cuando termina la animación de movimiento)
      setTimeout(() => {
        if (heroLogoRef.current) {
          heroLogoRef.current.style.transition = 'opacity 0.3s ease'
          heroLogoRef.current.style.opacity = '1'
        }
      }, 700)
    }, 1200)

    // Restaurar scroll solo después de que termine completamente la animación y se estabilice
    // 1200ms (espera) + 700ms (movimiento) + 500ms (estabilización) = 2400ms total
    const scrollTimer = setTimeout(() => {
      document.body.classList.remove('no-scroll')
    }, 2400)

    return () => {
      clearTimeout(moveTimer)
      clearTimeout(scrollTimer)
      // Asegurar que el scroll se restaure al desmontar
      document.body.classList.remove('no-scroll')
    }
  }, [heroLogoRef])

  return (
    <div
      ref={splashContainerRef}
      className="fixed inset-0 z-[9999] bg-white flex items-center justify-center overflow-hidden"
    >
      <motion.div
        ref={splashCircleRef}
        className="w-5 h-5 bg-[#E2001A] rounded-full absolute"
        initial={{ scale: 1 }}
        animate={{ scale: 200 }}
        transition={{
          duration: 0.6,
          ease: 'easeIn',
        }}
      />

      <motion.div
        className="relative z-[10000]"
        initial={{ opacity: 0, scale: 1.1 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          duration: 0.3,
          delay: 0.6,
          ease: 'easeOut',
        }}
      >
        <img
          ref={splashLogoRef}
          src="/assets/images/Logo APP 2.svg"
          alt="Ruky Logo"
          className="w-[150px] h-auto"
        />
      </motion.div>
    </div>
  )
}

export default SplashScreen
